import React, { useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Mail,
  MessageCircle,
  Send,
  ShieldCheck,
  Star,
} from "lucide-react";

const ReviewPage = () => {
  const { patientId } = useParams();

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const whatsappNumber = "2348069804062";

  const ratingLabel = useMemo(() => {
    const activeRating = hover || rating;

    if (activeRating === 1) return "Poor";
    if (activeRating === 2) return "Fair";
    if (activeRating === 3) return "Good";
    if (activeRating === 4) return "Very good";
    if (activeRating === 5) return "Excellent";

    return "Select a rating";
  }, [hover, rating]);

  const whatsappText = useMemo(() => {
    return encodeURIComponent(
      `Hello Zaviramed, I just submitted a review for my consultation.`
    );
  }, []);

  const openWhatsapp = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=${whatsappText}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleSubmit = async () => {
    try {
      setErrorMessage("");

      if (!rating) {
        setErrorMessage("Please select a rating before submitting your review.");
        return;
      }

      if (!patientId) {
        setErrorMessage(
          "We could not identify this patient review link. Please contact Zaviramed support."
        );
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      if (!backendUrl) {
        setErrorMessage(
          "Review submission is not configured correctly. Please contact Zaviramed support."
        );
        return;
      }

      setLoading(true);

      await axios.post(
        `${backendUrl}/reviews`,
        {
          patientId,
          rating,
          comment: comment.trim(),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000,
        }
      );

      setSubmitted(true);
    } catch (err) {
      console.error("Review submission error:", err);

      if (err.code === "ECONNABORTED") {
        setErrorMessage(
          "The request took too long. Please check your internet connection and try again."
        );
        return;
      }

      setErrorMessage(
        err.response?.data?.message ||
          "We could not submit your review. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-[#F8FAFC] text-slate-950">
        <section className="mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid w-full overflow-hidden rounded-[28px] border border-slate-200 bg-white lg:grid-cols-[0.95fr_1.05fr]">
            <aside className="hidden border-r border-slate-200 bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
              <div>
                <img
                  src="/logo_new_nobg.png"
                  alt="Zaviramed"
                  className="h-12 w-auto object-contain"
                />

                <div className="mt-16">
                  <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-emerald-300">
                    <CheckCircle2 size={15} />
                    Feedback received
                  </div>

                  <h1 className="max-w-md text-4xl font-semibold tracking-[-0.04em] text-white">
                    Thank you for helping us improve patient care.
                  </h1>

                  <p className="mt-5 max-w-sm text-sm leading-7 text-slate-300">
                    Your feedback helps us improve doctor response, consultation
                    quality, and the overall Zaviramed experience.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 text-sm text-slate-300">
                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Service</span>
                  <span className="font-medium text-white">Consultation</span>
                </div>

                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Rating</span>
                  <span className="font-medium text-white">{rating}/5</span>
                </div>

                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Status</span>
                  <span className="font-medium text-emerald-300">Submitted</span>
                </div>
              </div>
            </aside>

            <section className="p-5 sm:p-8 md:p-10 lg:p-12">
              <div className="mb-10 flex items-center justify-between gap-4 lg:hidden">
                <img
                  src="/logo_new_nobg.png"
                  alt="Zaviramed"
                  className="h-10 w-auto object-contain"
                />

                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                  <ShieldCheck size={14} />
                  Secure
                </div>
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                <CheckCircle2 size={14} />
                Review submitted
              </div>

              <div className="mt-8 max-w-2xl">
                <div className="mb-7 flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-700">
                  <CheckCircle2 size={32} />
                </div>

                <h2 className="text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl">
                  Thank you for your feedback
                </h2>

                <p className="mt-4 text-base leading-7 text-slate-600">
                  Your review has been received. We appreciate you taking the
                  time to share your experience with Zaviramed.
                </p>

                <div className="mt-8 rounded-2xl border border-slate-200">
                  <div className="border-b border-slate-200 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Review summary
                    </p>
                  </div>

                  <div className="grid gap-0 sm:grid-cols-2">
                    <div className="border-b border-slate-200 px-4 py-4 sm:border-b-0 sm:border-r">
                      <p className="text-xs font-medium text-slate-500">
                        Rating
                      </p>

                      <div className="mt-2 flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={18}
                            className={
                              rating >= star
                                ? "fill-amber-400 text-amber-400"
                                : "text-slate-300"
                            }
                          />
                        ))}
                      </div>
                    </div>

                    <div className="px-4 py-4">
                      <p className="text-xs font-medium text-slate-500">
                        Experience
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">
                        {ratingLabel}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={openWhatsapp}
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0F8F5F] px-5 py-4 text-sm font-semibold text-white transition hover:bg-[#0B7A50] active:scale-[0.99] sm:w-auto"
                >
                  <MessageCircle size={18} />
                  Continue on WhatsApp
                </button>
              </div>
            </section>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F8FAFC] text-slate-950">
      <section className="mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-slate-200 bg-white lg:grid-cols-[0.95fr_1.05fr]">
          <aside className="hidden border-r border-slate-200 bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
            <div>
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-12 w-auto object-contain"
              />

              <div className="mt-16">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-emerald-300">
                  <ShieldCheck size={15} />
                  Patient feedback
                </div>

                <h1 className="max-w-md text-4xl font-semibold tracking-[-0.04em] text-white">
                  Tell us how your consultation went.
                </h1>

                <p className="mt-5 max-w-sm text-sm leading-7 text-slate-300">
                  Your review helps Zaviramed improve response quality, doctor
                  communication, and patient experience.
                </p>
              </div>
            </div>

            <div className="grid gap-3 text-sm text-slate-300">
              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Service</span>
                <span className="font-medium text-white">Consultation</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Provider</span>
                <span className="font-medium text-white">Zaviramed</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Review</span>
                <span className="font-medium text-white">Patient feedback</span>
              </div>
            </div>
          </aside>

          <section className="p-5 sm:p-8 md:p-10 lg:p-12">
            <div className="mb-10 flex items-center justify-between gap-4 lg:hidden">
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-10 w-auto object-contain"
              />

              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                <ShieldCheck size={14} />
                Secure
              </div>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
              <MessageCircle size={14} />
              Consultation review
            </div>

            <div className="mt-8 max-w-2xl">
              <h2 className="text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl">
                How was your consultation?
              </h2>

              <p className="mt-4 text-base leading-7 text-slate-600">
                Rate your experience and leave a short comment. Your feedback
                helps us improve care delivery for every patient.
              </p>
            </div>

            <div className="mt-8 rounded-2xl border border-slate-200">
              <div className="border-b border-slate-200 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Your rating
                </p>
              </div>

              <div className="px-4 py-6">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const active = (hover || rating) >= star;

                      return (
                        <button
                          key={star}
                          type="button"
                          onClick={() => {
                            setRating(star);
                            setErrorMessage("");
                          }}
                          onMouseEnter={() => setHover(star)}
                          onMouseLeave={() => setHover(null)}
                          className="rounded-xl border border-transparent p-1 transition hover:border-slate-200 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                          aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                        >
                          <Star
                            size={34}
                            className={
                              active
                                ? "fill-amber-400 text-amber-400"
                                : "text-slate-300"
                            }
                          />
                        </button>
                      );
                    })}
                  </div>

                  <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700">
                    {ratingLabel}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200">
              <div className="border-b border-slate-200 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Comment
                </p>
              </div>

              <div className="px-4 py-4">
                <textarea
                  placeholder="Tell us what went well or what we can improve. This is optional."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={5}
                  maxLength={600}
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                />

                <div className="mt-2 flex justify-end text-xs text-slate-400">
                  {comment.length}/600
                </div>
              </div>
            </div>

            {errorMessage && (
              <div className="mt-6 flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-700">
                <AlertCircle className="mt-0.5 shrink-0" size={18} />
                <p>{errorMessage}</p>
              </div>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0F8F5F] px-5 py-4 text-sm font-semibold text-white transition hover:bg-[#0B7A50] active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
                {loading ? "Submitting review" : "Submit review"}
              </button>

              <button
                type="button"
                onClick={() => {
                      window.location.href = "mailto:support@zaviramed.com.ng";
                    }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-4 text-sm font-semibold text-slate-950 transition hover:bg-slate-50 active:scale-[0.99] sm:w-auto"
              >
                <Mail size={18} />
                 support
              </button>
            </div>

            <div className="mt-10 grid gap-3 border-t border-slate-200 pt-6 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" />
                <p>
                  Your review helps us improve Zaviramed’s doctor response and
                  patient care experience.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" />
                <p>
                  Please do not include highly sensitive information in the review
                  comment unless necessary.
                </p>
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default ReviewPage;